<script setup lang="ts">
import { computed, ref } from 'vue';
import { AlertCircle, ChevronDown } from 'lucide-vue-next';
import BaseIcon from './BaseIcon.vue';

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

interface Props {
  modelValue: string | number | null;
  options: SelectOption[];
  label?: string;
  error?: string;
  helperText?: string;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  id?: string;
}

const emit = defineEmits<{
  'update:modelValue': [value: string | number | null];
  focus: [];
  blur: [];
  change: [];
}>();

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  required: false,
  placeholder: 'Select an option...',
});

// Generate unique ID if not provided
const selectId = computed(() => props.id || `select-${Math.random().toString(36).substr(2, 9)}`);

// Track if field has been touched for error display
const isTouched = ref(false);

const hasError = computed(() => !!props.error);

const selectClass = computed(() => {
  const baseClasses = [
    'w-full',
    'px-sm',
    'py-xs',
    'pr-10',
    'bg-bg-tertiary',
    'border',
    'border-border-light',
    'rounded-md',
    'text-text-primary',
    'transition-all',
    'duration-base',
    'ease-default',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-primary',
    'focus:border-transparent',
    'appearance-none',
    'cursor-pointer',
    'text-base',
  ];

  // Error state styling
  if (hasError.value) {
    baseClasses.push('border-error', 'focus:ring-error');
  }

  // Disabled state
  if (props.disabled) {
    baseClasses.push('opacity-50', 'cursor-not-allowed', 'bg-bg-secondary');
  }

  // Placeholder color
  if (!props.modelValue && props.placeholder) {
    baseClasses.push('placeholder:text-text-tertiary');
  }

  // Error shake animation
  if (hasError.value && isTouched.value) {
    baseClasses.push('animate-shake-once');
  }

  return baseClasses.join(' ');
});

const wrapperClass = computed(() => {
  const baseClasses = ['relative'];
  return baseClasses.join(' ');
});

const iconWrapperClass = computed(() => {
  const baseClasses = [
    'absolute',
    'right-3',
    'top-1/2',
    '-translate-y-1/2',
    'pointer-events-none',
    'text-text-tertiary',
  ];

  // Rotate icon based on state
  return baseClasses.join(' ');
});

const labelClass = computed(() => {
  return [
    'block',
    'text-sm',
    'font-medium',
    'mb-1',
    'text-text-secondary',
  ].join(' ');
});

const errorClass = computed(() => [
  'text-sm',
  'text-error',
  'mt-1',
  'flex',
  'items-center',
  'gap-1',
].join(' '));

const helperClass = computed(() => [
  'text-xs',
  'text-text-tertiary',
  'mt-1',
].join(' '));

const requiredIndicatorClass = computed(() => [
  'text-error',
  'ml-0.5',
].join(' '));

// Event handlers
const handleChange = (event: Event) => {
  const target = event.target as HTMLSelectElement;
  const value = target.value ? (target.value as string | number) : null;
  emit('update:modelValue', value);
  emit('change');
};

const handleFocus = () => {
  emit('focus');
};

const handleBlur = () => {
  isTouched.value = true;
  emit('blur');
};

// ARIA attributes
const ariaDescribedBy = computed(() => {
  const describedBy: string[] = [];

  if (hasError.value) {
    describedBy.push(`${selectId.value}-error`);
  }

  if (!hasError.value && props.helperText) {
    describedBy.push(`${selectId.value}-helper`);
  }

  return describedBy.length > 0 ? describedBy.join(' ') : undefined;
});
</script>

<template>
  <div class="space-y-1">
    <!-- Label -->
    <label v-if="label" :for="selectId" :class="labelClass">
      {{ label }}
      <span v-if="required" :class="requiredIndicatorClass">*</span>
    </label>

    <!-- Select wrapper -->
    <div :class="wrapperClass">
      <select
        :id="selectId"
        :value="modelValue ?? ''"
        :disabled="disabled"
        :required="required"
        :class="selectClass"
        @change="handleChange"
        @focus="handleFocus"
        @blur="handleBlur"
        :aria-invalid="hasError"
        :aria-describedby="ariaDescribedBy"
        :aria-required="required"
      >
        <!-- Placeholder option -->
        <option v-if="placeholder" value="" disabled selected>
          {{ placeholder }}
        </option>

        <!-- Options -->
        <option
          v-for="option in options"
          :key="option.value"
          :value="option.value"
          :disabled="option.disabled"
        >
          {{ option.label }}
        </option>
      </select>

      <!-- Chevron down icon -->
      <div :class="iconWrapperClass">
        <BaseIcon :icon="ChevronDown" size="sm" :aria-hidden="true" />
      </div>
    </div>

    <!-- Error message -->
    <p v-if="hasError" :id="`${selectId}-error`" :class="errorClass" role="alert">
      <BaseIcon :icon="AlertCircle" size="sm" :aria-hidden="true" />
      {{ error }}
    </p>

    <!-- Helper text -->
    <p v-else-if="helperText" :id="`${selectId}-helper`" :class="helperClass">
      {{ helperText }}
    </p>
  </div>
</template>

<style scoped>
.animate-shake-once {
  animation: shake 0.5s ease-in-out;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
  20%, 40%, 60%, 80% { transform: translateX(4px); }
}

/* Dark mode adjustments */
.dark .animate-shake-once {
  animation: shake-dark 0.5s ease-in-out;
}

@keyframes shake-dark {
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
  20%, 40%, 60%, 80% { transform: translateX(4px); }
}
</style>
